/**
 * La feuille de l'énoncé — une seule, lue par les deux moteurs.
 *
 * Porte `tutor/home/MarkdownWithLatex.web.css` et le bloc `ENONCE_BODY_CSS` de
 * `tutor/home/MarkdownWithLatex.js` — deux copies à garder en phase à la main (« Mirror these
 * rules in the mobile srcDoc so the two engines stay visually identical »). Ici la règle s'écrit
 * une fois pour les DEUX moteurs du design system : le natif la pose dans le `<style>` du
 * document de la WebView, le web dans une balise `<style>` du document hôte, et les deux lisent
 * les mêmes variables CSS.
 *
 * Les deux copies legacy, elles, VIVENT encore : les écrans de correction les montent toujours
 * (`CorrectionPointCard`, `MaterialCorrectionDisplay`). Tant qu'ils ne sont pas basculés sur
 * `<RichText>`, la règle existe en TROIS exemplaires, pas un — et une retouche ici ne les suit pas.
 *
 * Les variables sont la couture de personnalisation du legacy (`--enonce-*`) ; les couleurs et
 * les tailles viennent des jetons du thème, jamais d'une valeur en dur. La police, elle, est
 * PT Serif et non la face de l'app : l'énoncé est une feuille d'exercice, avec sa propre lecture
 * — la même chez l'élève (ancien design) et chez le parent (design system), et celle que le
 * legacy servait déjà.
 *
 * Les classes `enonce-*` ne se renomment PAS : ce sont celles que le markdown du tuteur et de
 * l'IA écrit déjà, et que l'assainissement laisse passer.
 *
 * Une seconde variante, `message`, habille une bulle de conversation (une réponse de Sam) : la
 * face du design system (Outfit), comme le reste du chat, sans les repères d'un énoncé — une liste
 * numérotée reste une liste, sans pastilles ni sous-questions (Wajih, 21/10).
 */
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { highlighter, type HighlighterBand } from "../../theme/tokens/effects"
import { type SpacingKey } from "../../theme/tokens/spacing"
import { type FontFamilyKey, type FontSizeKey } from "../../theme/tokens/typography"
import { FIGURE_CAP_FROM, FIGURE_FLOOR, FIGURE_SHARE } from "./figureWidth"

/** La classe qui porte l'énoncé, des deux côtés. */
export const RICH_TEXT_CLASS = "masteur-rich-text"

/**
 * Les classes `enonce-*` que l'assainissement du web laisse passer — écrites ici une seule fois :
 * la feuille en dérive ses règles, et `RichText.web` en construit son expression d'allowlist.
 * Recopiée, la liste finissait par autoriser une classe qu'aucune règle n'habille, ou l'inverse.
 *
 *  - les cinq de la palette (`primary`…`highlight`), que le tuteur écrit déjà : elles ne se
 *    renomment PAS ;
 *  - les quatre RÔLES que le générateur pose (`consigne`, `donnee`, `notion`, `attention`) : ils
 *    disent ce qu'est le mot, pas sa couleur — une ancienne version de l'app qui ne les connaît
 *    pas en retire la classe et garde le texte ;
 *  - `sous`, l'étiquette d'une sous-question « a) », posée à l'affichage par `markEnonceStructure` ;
 *  - `a-utiliser` et son titre, l'encadré des données de la lecture adaptée, posé à l'affichage
 *    par `withDataBox`.
 */
export const ENONCE_CLASSES = [
    "primary",
    "accent",
    "success",
    "error",
    "highlight",
    "consigne",
    "donnee",
    "notion",
    "attention",
    "sous",
    "a-utiliser",
    "a-utiliser-titre",
] as const

/** Ce que le composant doit mesurer pour dimensionner sa WebView. */
export const RICH_TEXT_CONTENT_ID = "content"

/**
 * Ce que la feuille lit dans le thème : les jetons, par leurs VRAIES clés.
 *
 * Pas `AppTheme` : celui-ci est le socle `as const`, donc ses valeurs sont des littéraux
 * (`primary: "#1A2B66"`), et un thème de persona qui repointe un jeton ne lui est pas
 * assignable. Les clés, elles, sont bien celles des jetons — ce qui suffit à faire tomber les
 * `!` qu'un `Record<string, …>` imposait.
 */
type RichTextTheme = {
    colors: Record<SemanticColorKey, string>
    fontFamilies: Record<FontFamilyKey, string>
    fontSizes: Record<FontSizeKey, number>
    spacing: Record<SpacingKey, number>
}

/**
 * Le surligneur de l'énoncé pour le thème courant — ce que rend `atoms/Highlight/useHighlighterBand`.
 * `ink` nul : le mot garde l'encre du texte qui l'entoure.
 */
export type RichTextHighlight = { band: HighlighterBand; ink: SemanticColorKey | null }

/**
 * La face de l'énoncé : PT Serif, inlinée des deux côtés (`assets/vendor/ptserif.*`) en 400 et
 * 700, romain et italique — une seule famille, `font-weight` choisit la graisse.
 */
const ENONCE_FONT_STACK = `"PT Serif", Georgia, serif`

/**
 * Comment l'élève lit son énoncé (sa fiche « Ma lecture ») : la police, la taille en px, et la
 * lecture adaptée — ses espacements et sa mise en page, portés par `ADAPTED_READING_CLASS`.
 * Absente, la feuille est celle du lot 1, telle que le tuteur et le parent la voient.
 */
export type RichTextReading = { adapted: boolean; font: "standard" | "lexend"; size: number }

/** Lexend, inlinée comme PT Serif (`assets/vendor/lexend.*`), en 400 et 700 sans italique. */
const READING_FONT_STACKS: Record<RichTextReading["font"], string> = {
    standard: ENONCE_FONT_STACK,
    lexend: `Lexend, Arial, sans-serif`,
}

/** La classe de racine de la lecture adaptée : les questions en blocs, l'encadré, pas d'italique. */
export const ADAPTED_READING_CLASS = "enonce-lecture-adaptee"

/**
 * Ce que porte le texte : un énoncé (la feuille d'exercice, en PT Serif, avec ses repères), ou un
 * message (une bulle de conversation, en Outfit, sans les repères ni la lecture de l'élève).
 */
export type RichTextVariant = "enonce" | "message"

/** La classe de racine d'un message : la liste reste une liste, sans pastilles. */
export const MESSAGE_CLASS = "masteur-rich-text-message"

/** La classe de la racine, pour les deux moteurs : la feuille, la lecture adaptée, ou le message. */
export function richTextRootClass(reading?: RichTextReading, variant: RichTextVariant = "enonce"): string {
    if (variant === "message") return `${RICH_TEXT_CLASS} ${MESSAGE_CLASS}`
    return reading?.adapted === true ? `${RICH_TEXT_CLASS} ${ADAPTED_READING_CLASS}` : RICH_TEXT_CLASS
}

/**
 * La largeur moyenne d'un caractère d'un texte français, espaces compris, en em — mesurée dans le
 * navigateur sur les polices de l'énoncé, AVEC l'espacement de la lecture adaptée
 * (`readingCssVars`) : changer l'un oblige à remesurer l'autre.
 */
const ADAPTED_CHAR_WIDTH_EM: Record<RichTextReading["font"], number> = { standard: 0.53, lexend: 0.57 }

/** 60 à 70 caractères par ligne, pour le guide de la British Dyslexia Association. */
const ADAPTED_LINE_CHARS = 65

/** La largeur, en px, d'une ligne de lecture adaptée : l'écran qui porte la feuille s'y resserre. */
export function adaptedLineWidth(reading: RichTextReading): number {
    return Math.round(ADAPTED_LINE_CHARS * reading.size * ADAPTED_CHAR_WIDTH_EM[reading.font])
}

/**
 * Les variables d'une lecture : la face et la taille, et les titres qui suivent la taille — sinon
 * un énoncé en 21 px aurait des titres plus petits que son texte. La lecture adaptée écarte les
 * lettres ET les mots (écarter les lettres seules ralentit la lecture) et grossit la pastille.
 */
function readingCssVars(theme: RichTextTheme, reading: RichTextReading): Record<string, string> {
    const vars: Record<string, string> = {
        "--enonce-font-family": READING_FONT_STACKS[reading.font],
        "--enonce-font-size": `${reading.size}px`,
        "--enonce-font-size-h1": "1.3em",
        "--enonce-font-size-h2": "1.15em",
        "--enonce-font-size-h3": "1em",
    }
    if (!reading.adapted) return vars
    return {
        ...vars,
        "--enonce-letter-spacing": "0.06em",
        "--enonce-word-spacing": "0.2em",
        "--enonce-number-size": `${theme.spacing["8"]}px`,
        "--enonce-font-size-number": `${theme.fontSizes["text-md"]}px`,
    }
}

/**
 * Les variables d'un message : la face du chat, à la taille d'une bulle (`text-lg`), et des écarts
 * de conversation, plus serrés que ceux d'une feuille. Outfit est enregistrée une famille par
 * graisse (`theme.fontFamilies`) : un gras change de famille (`--enonce-font-family-bold`), sinon
 * le moteur épaissirait la graisse 400. Le web lit les faces que l'app charge ; le natif, celles que
 * son document inline (`assets/vendor/outfit.*`).
 */
function messageCssVars(theme: RichTextTheme): Record<string, string> {
    return {
        "--enonce-font-family": `${theme.fontFamilies.regular}, sans-serif`,
        "--enonce-font-family-bold": `${theme.fontFamilies.bold}, sans-serif`,
        "--enonce-font-size-h1": `${theme.fontSizes["text-xl"]}px`,
        "--enonce-font-size-h2": `${theme.fontSizes["text-lg"]}px`,
        "--enonce-font-size-h3": `${theme.fontSizes["text-lg"]}px`,
        "--enonce-line-height": "1.4",
        "--enonce-block-gap": `${theme.spacing["3"]}px`,
        "--enonce-list-gap": `${theme.spacing["2"]}px`,
    }
}

/**
 * Les variables, depuis les jetons — et, pour l'élève, depuis sa lecture. Un message ne lit pas la
 * lecture de l'élève : sa fiche « Ma lecture » règle ses énoncés, pas le chat.
 */
export function richTextCssVars(
    theme: RichTextTheme,
    highlight: RichTextHighlight,
    reading?: RichTextReading,
    variant: RichTextVariant = "enonce",
    color?: SemanticColorKey,
    align?: "start"
): Record<string, string> {
    const own = variant === "message" ? messageCssVars(theme) : reading != null ? readingCssVars(theme, reading) : {}
    return {
        ...baseCssVars(theme, highlight),
        ...own,
        // L'encre du texte posée par l'écran (une réponse de quiz choisie, sur fond coloré) ; les formules
        // la suivent (`currentColor`). Et le début de la ligne, quand l'écran le demande.
        ...(color != null ? { "--enonce-color-text": theme.colors[color] } : {}),
        ...(align != null ? { "--enonce-text-align": align } : {}),
    }
}

function baseCssVars(theme: RichTextTheme, highlight: RichTextHighlight): Record<string, string> {
    const { band, ink } = highlight
    return {
        "--enonce-font-family": ENONCE_FONT_STACK,
        "--enonce-font-size": `${theme.fontSizes["text-lg"]}px`,
        "--enonce-font-size-h1": `${theme.fontSizes["text-2xl"]}px`,
        "--enonce-font-size-h2": `${theme.fontSizes["text-xl"]}px`,
        "--enonce-font-size-h3": `${theme.fontSizes["text-lg"]}px`,
        "--enonce-font-size-caption": `${theme.fontSizes["text-md"]}px`,
        "--enonce-line-height": "1.5",
        "--enonce-block-gap": `${theme.spacing["4"]}px`,
        "--enonce-list-indent": `${theme.spacing["6"]}px`,
        "--enonce-list-gap": `${theme.spacing["5"]}px`,
        "--enonce-inline-gap": `${theme.spacing["2"]}px`,
        "--enonce-pad": `${theme.spacing["4"]}px`,
        "--enonce-pad-sm": `${theme.spacing["3"]}px`,
        "--enonce-radius": `${theme.spacing["2"]}px`,
        "--enonce-color-text": theme.colors["text-primary"],
        "--enonce-color-muted": theme.colors["text-muted"],
        "--enonce-color-border": theme.colors["border-card"],
        "--enonce-color-surface": theme.colors["surface-subtle"],
        "--enonce-color-primary": theme.colors.primary,
        "--enonce-color-accent": theme.colors["accent-strong"],
        "--enonce-color-success": theme.colors["success-strong"],
        "--enonce-color-error": theme.colors["danger-strong"],
        // Les repères de la feuille : de l'encre, du gris, le jaune du surligneur et l'orange
        // d'alerte — aucune couleur d'univers, pour que la feuille se lise pareil dans l'ancien
        // design de l'élève et dans le design system du parent.
        "--enonce-color-number-ink": theme.colors.surface,
        "--enonce-color-data-border": theme.colors.border,
        "--enonce-color-warning-bg": theme.colors.warningLight,
        "--enonce-color-warning-ink": theme.colors["warning-strong"],
        "--enonce-warning-glyph": warningGlyph(theme.colors["warning-strong"]),
        "--enonce-number-size": `${theme.spacing["6"]}px`,
        "--enonce-font-size-number": `${theme.fontSizes["text-sm"]}px`,
        // Le surligneur du livre (`.hl` du kit) : la même recette que `<Highlight>`, lue dans
        // `tokens/effects`. `currentColor` sur `color` garde l'encre du texte autour.
        "--enonce-color-highlight": theme.colors.accent,
        "--enonce-highlight-ink": ink != null ? theme.colors[ink] : "currentColor",
        "--enonce-highlight-pad": `${band.padX}em`,
        "--enonce-highlight-inset": `${band.insetX}em`,
        "--enonce-highlight-top": typeof band.top === "number" ? `${band.top}em` : band.top,
        "--enonce-highlight-bottom": `${band.bottom}em`,
        "--enonce-highlight-radius": `${highlighter.radius}em`,
        "--enonce-highlight-rotate": `${highlighter.rotate}deg`,
        "--enonce-bg": "transparent",
    }
}

/** Le même jeu de variables, écrit en bloc `:root` — la WebView n'a pas de style en ligne. */
export function richTextRootCss(
    theme: RichTextTheme,
    highlight: RichTextHighlight,
    reading?: RichTextReading,
    variant: RichTextVariant = "enonce",
    color?: SemanticColorKey,
    align?: "start"
): string {
    const vars = richTextCssVars(theme, highlight, reading, variant, color, align)
    const lines = Object.keys(vars).map((name) => `  ${name}: ${vars[name]};`)
    return `:root {\n${lines.join("\n")}\n}`
}

/**
 * Une liste reprise après un paragraphe sort en `<ol start="3">` : un compteur CSS ignore
 * l'attribut, il faut donc le recaler. Trente questions couvrent largement un énoncé.
 */
const OL_START_RULES = Array.from({ length: 29 }, (_, i) => i + 2)
    .map((n) => `.${RICH_TEXT_CLASS} ol[start="${n}"] { counter-reset: enonce-q ${n - 1}; }`)
    .join("\n")

/**
 * Le triangle du piège, en image de fond peinte dans l'encre du piège — pas d'emoji, et pas de
 * masque CSS : la WebView d'iOS peignait la couleur du masque hors du triangle, qui sortait
 * dans un carré plein.
 */
function warningGlyph(ink: string): string {
    const svg =
        `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${ink}' ` +
        "stroke-width='2.6' stroke-linecap='round' stroke-linejoin='round'>" +
        "<path d='M12 3l10 18H2L12 3z'/><path d='M12 10v5M12 18h.01'/></svg>"
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`
}

/**
 * Les règles, identiques des deux côtés. Tout est relatif aux variables ci-dessus : une règle
 * qui écrirait une couleur ou une taille ici rouvrirait l'écart que ce fichier ferme.
 */
export const RICH_TEXT_CSS = `
.${RICH_TEXT_CLASS} {
  font-family: var(--enonce-font-family);
  font-size: var(--enonce-font-size);
  line-height: var(--enonce-line-height);
  color: var(--enonce-color-text);
  background: var(--enonce-bg);
  letter-spacing: var(--enonce-letter-spacing, normal);
  word-spacing: var(--enonce-word-spacing, normal);
  word-wrap: break-word;
  overflow-wrap: break-word;
  /* L'alignement de l'endroit où il est posé (variable absente : la valeur héritée), sauf quand
     l'écran demande le début de la ligne (prop align). */
  text-align: var(--enonce-text-align);
}
.${RICH_TEXT_CLASS} > *:not(:last-child) { margin-bottom: var(--enonce-block-gap); }
.${RICH_TEXT_CLASS} h1,
.${RICH_TEXT_CLASS} h2,
.${RICH_TEXT_CLASS} h3 {
  font-weight: 700;
  margin: 0 0 var(--enonce-block-gap);
}
.${RICH_TEXT_CLASS} h1 { font-size: var(--enonce-font-size-h1); }
.${RICH_TEXT_CLASS} h2 { font-size: var(--enonce-font-size-h2); }
.${RICH_TEXT_CLASS} h3 { font-size: var(--enonce-font-size-h3); }
.${RICH_TEXT_CLASS} p { margin: 0; }
/* Le gras : le markdown, et les repères qui n'ont pas d'autre forme que lui (la consigne) ou qui
   le portent en plus de la leur (donnée, piège, étiquette de sous-question). */
.${RICH_TEXT_CLASS} strong,
.${RICH_TEXT_CLASS} b,
.${RICH_TEXT_CLASS} .enonce-consigne,
.${RICH_TEXT_CLASS} .enonce-donnee,
.${RICH_TEXT_CLASS} .enonce-attention,
.${RICH_TEXT_CLASS} .enonce-sous { font-weight: 700; }
.${RICH_TEXT_CLASS} ul { margin: 0; padding-left: var(--enonce-list-indent); }
.${RICH_TEXT_CLASS} ol > li:not(:last-child),
.${RICH_TEXT_CLASS} ul > li:not(:last-child) { margin-bottom: var(--enonce-list-gap); }
.${RICH_TEXT_CLASS} ol > li br { display: block; margin-top: var(--enonce-inline-gap); }
/* L'écart court entre les blocs d'une question (ses sous-questions rangées sous elle par
   \`markEnonceStructure\`) et entre ceux d'une citation. */
.${RICH_TEXT_CLASS} li > *:not(:last-child),
.${RICH_TEXT_CLASS} blockquote > *:not(:last-child) { margin-bottom: var(--enonce-inline-gap); }
/* Les questions numérotées : le numéro devient une pastille, sur TOUS les énoncés texte — c'est
   de la structure, pas une marque du générateur. Le compteur suit l'attribut \`start\` d'une liste
   reprise après un paragraphe (« 3. » après un texte), d'où les règles \`ol[start]\`. */
.${RICH_TEXT_CLASS} ol { margin: 0; padding-left: 0; list-style: none; counter-reset: enonce-q; }
${OL_START_RULES}
.${RICH_TEXT_CLASS} ol > li {
  counter-increment: enonce-q;
  position: relative;
  padding-left: calc(var(--enonce-number-size) + var(--enonce-pad-sm));
}
.${RICH_TEXT_CLASS} ol > li::before {
  content: counter(enonce-q);
  position: absolute;
  left: 0;
  top: calc((var(--enonce-line-height) * 1em - var(--enonce-number-size)) / 2);
  width: var(--enonce-number-size);
  height: var(--enonce-number-size);
  border-radius: 9999px;
  background: var(--enonce-color-text);
  color: var(--enonce-color-number-ink);
  font-weight: 700;
  font-size: var(--enonce-font-size-number);
  line-height: var(--enonce-number-size);
  letter-spacing: 0;
  text-align: center;
}
/* L'étiquette d'une sous-question « a) » (posée par \`markEnonceStructure\`). */
.${RICH_TEXT_CLASS} .enonce-sous { color: var(--enonce-color-muted); }
/* Le texte à travailler — la citation, la phrase à corriger — s'écrit en citation Markdown :
   un encadré, et rien n'y est jamais marqué. */
.${RICH_TEXT_CLASS} blockquote {
  margin: 0;
  padding: var(--enonce-pad-sm) var(--enonce-pad);
  background: var(--enonce-color-surface);
  border-left: 4px solid var(--enonce-color-data-border);
  border-radius: 0 var(--enonce-radius) var(--enonce-radius) 0;
}
.${RICH_TEXT_CLASS} a { color: var(--enonce-color-primary); text-decoration: underline; }
.${RICH_TEXT_CLASS} .math.block,
.${RICH_TEXT_CLASS} .katex-display,
.${RICH_TEXT_CLASS} mjx-container[display="true"] {
  display: block;
  text-align: center;
  margin: var(--enonce-block-gap) 0;
}
/* Mathpix (le natif) dessine une formule en ligne dans un SVG collé à ses glyphes : deux lignes
   de fractions (« a) 9/14 » puis « b) 2/5 ») se touchaient. Une marge AU-DESSUS seulement agrandit
   la ligne qui porte une formule haute ; en dessous, elle remonterait la formule sur le texte. */
.${RICH_TEXT_CLASS} mjx-container:not([display="true"]) > svg { margin-top: 0.3em; }
/* KaTeX (le web) : une formule en bloc ne passe jamais à la ligne — sur un écran étroit, elle
   défile plutôt que de déborder de l'énoncé. Même garde que le legacy web et le BO ; le natif
   (Mathpix) n'est pas concerné. Les 2 px gardent le haut et le bas des glyphes que overflow-y rognerait. */
.${RICH_TEXT_CLASS} .katex-display {
  overflow-x: auto;
  overflow-y: hidden;
  padding: 2px 0;
}
.${RICH_TEXT_CLASS} table {
  border-collapse: collapse;
  width: 100%;
}
.${RICH_TEXT_CLASS} th,
.${RICH_TEXT_CLASS} td {
  border: 1px solid var(--enonce-color-border);
  padding: var(--enonce-inline-gap) var(--enonce-pad-sm);
  text-align: left;
}
.${RICH_TEXT_CLASS} th {
  font-weight: 700;
  background: var(--enonce-color-surface);
}
/* Une figure pleine largeur dans un bloc large devient très haute et pousse la consigne sous le pli :
   à partir d'un bloc de 600 px, elle se plafonne à sa moitié, sans passer sous 400 px (son plancher :
   l'activité agrandie du live la faisait rétrécir). La règle en nombres : figureWidth.ts. C'est la
   largeur du BLOC qui compte (le pourcentage de max-width), pas celle de la fenêtre : le volet du live
   au grand écran est étroit dans une fenêtre large, et la figure y garde toute sa largeur. Sous 600 px,
   (600px - 100%) * 1000 est grand et le min rend 100 % ; au-delà, il est négatif et le max rend la
   moitié, ou le plancher. Pas de requête de conteneur : elle demande une contention de largeur, qui
   écraserait un énoncé posé dans un bloc à la largeur de son contenu, et les WebViews anciennes ne la
   lisent pas. Un moteur sans max() garde la ligne d'avant, 100 %. */
.${RICH_TEXT_CLASS} img {
  display: block;
  max-width: 100%;
  max-width: max(min(100%, ${FIGURE_FLOOR}px), ${FIGURE_SHARE * 100}%, min(100%, (${FIGURE_CAP_FROM}px - 100%) * 1000));
  height: auto;
  margin: var(--enonce-pad-sm) auto;
  cursor: zoom-in;
}
.${RICH_TEXT_CLASS} figure { margin: var(--enonce-pad-sm) 0; }
.${RICH_TEXT_CLASS} figure img { margin: 0 auto; }
.${RICH_TEXT_CLASS} figcaption {
  font-size: var(--enonce-font-size-caption);
  color: var(--enonce-color-muted);
  text-align: center;
  margin-top: var(--enonce-inline-gap);
}
/* Écran large : un garde-fou de hauteur pour les images portrait. */
@media (min-width: 800px) {
  .${RICH_TEXT_CLASS} img { max-height: 60vh; }
}
.${RICH_TEXT_CLASS} .masteur-rich-text-broken {
  padding: var(--enonce-pad);
  background: var(--enonce-color-surface);
  border: 1px dashed var(--enonce-color-border);
  border-radius: var(--enonce-radius);
  color: var(--enonce-color-muted);
  font-style: italic;
  text-align: center;
}
.${RICH_TEXT_CLASS} code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  background: var(--enonce-color-surface);
  padding: 2px var(--enonce-inline-gap);
  border-radius: 3px;
}
.${RICH_TEXT_CLASS} pre {
  background: var(--enonce-color-surface);
  padding: var(--enonce-pad-sm);
  border-radius: var(--enonce-radius);
  overflow-x: auto;
}
.${RICH_TEXT_CLASS} pre code { background: transparent; padding: 0; }
/* Palette sémantique du markdown tuteur / IA — les cinq classes de couleur d'ENONCE_CLASSES. */
.${RICH_TEXT_CLASS} .enonce-primary { color: var(--enonce-color-primary); font-weight: 600; }
.${RICH_TEXT_CLASS} .enonce-accent { color: var(--enonce-color-accent); font-weight: 600; }
.${RICH_TEXT_CLASS} .enonce-success { color: var(--enonce-color-success); font-weight: 600; }
.${RICH_TEXT_CLASS} .enonce-error { color: var(--enonce-color-error); font-weight: 600; }
/* Les quatre rôles que pose le générateur : ce que l'élève doit FAIRE (consigne), ce qu'il doit
   UTILISER (donnée), le mot du cours (notion — le surligneur, plus bas), le PIÈGE (attention).
   Jamais deux effets sur un même mot : chacun a une seule forme — la consigne n'a que le gras
   (plus haut). */
.${RICH_TEXT_CLASS} .enonce-donnee,
.${RICH_TEXT_CLASS} .enonce-attention {
  border-radius: var(--enonce-radius);
  padding: 0 0.3em;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
}
/* Une donnée passe à la ligne comme le texte (le fond suit, \`box-decoration-break\`) : une
   donnée longue — un titre de texte officiel — débordait de la carte. */
.${RICH_TEXT_CLASS} .enonce-donnee {
  background: var(--enonce-color-surface);
  border: 1px solid var(--enonce-color-data-border);
}
/* Une donnée qui porte une formule l'entoure en entier : en ligne, sa bordure suit la hauteur du
   texte et barrait le numérateur d'une fraction (lecture adaptée surtout, où elle est plus foncée).
   Un peu d'air en haut et en bas : la fraction ne touche pas la bordure. */
.${RICH_TEXT_CLASS} .enonce-donnee:has(mjx-container, .katex) {
  display: inline-block;
  max-width: 100%;
  padding-top: 0.25em;
  padding-bottom: 0.25em;
}
.${RICH_TEXT_CLASS} .enonce-attention {
  color: var(--enonce-color-warning-ink);
  background: var(--enonce-color-warning-bg);
}
/* Le triangle tient sur des espaces insécables, pas sur un bloc : un bloc en ligne est un point
   de coupure, et le triangle restait seul en fin de ligne quand le piège passait à la suivante.
   Leur largeur ne suit pas l'écart de la lecture adaptée : il les élargissait d'un em de vide. */
.${RICH_TEXT_CLASS} .enonce-attention::before {
  content: "\\00a0\\00a0\\00a0\\00a0";
  letter-spacing: normal;
  word-spacing: normal;
  background: var(--enonce-warning-glyph) no-repeat left center / 0.85em 0.85em;
}
/* Le surligneur : une bande jaune DERRIÈRE le mot (sous son bas sur fond clair, sur tout le mot
   sur la nuit), jamais un fond de texte. Le mot ne se coupe pas : la bande ne sait pas suivre
   deux lignes. La notion clé du générateur EST un surligné. */
.${RICH_TEXT_CLASS} .enonce-notion,
.${RICH_TEXT_CLASS} .enonce-highlight {
  position: relative;
  z-index: 0;
  padding: 0 var(--enonce-highlight-pad);
  white-space: nowrap;
  color: var(--enonce-highlight-ink);
}
.${RICH_TEXT_CLASS} .enonce-notion::before,
.${RICH_TEXT_CLASS} .enonce-highlight::before {
  content: "";
  position: absolute;
  z-index: -1;
  left: var(--enonce-highlight-inset);
  right: var(--enonce-highlight-inset);
  top: var(--enonce-highlight-top);
  bottom: var(--enonce-highlight-bottom);
  background: var(--enonce-color-highlight);
  border-radius: var(--enonce-highlight-radius);
  transform: rotate(var(--enonce-highlight-rotate));
}
/* L'encadré « À utiliser » : les données de l'énoncé, regroupées en tête (\`withDataBox\`), en ligne
   souple, une donnée à côté de l'autre. Un span posé en bloc, pas un div : voir \`withDataBox\`. */
.${RICH_TEXT_CLASS} .enonce-a-utiliser {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--enonce-inline-gap);
  background: var(--enonce-color-surface);
  border-radius: var(--enonce-radius);
  padding: var(--enonce-pad-sm);
}
.${RICH_TEXT_CLASS} .enonce-a-utiliser-titre {
  flex-basis: 100%;
  font-weight: 700;
  color: var(--enonce-color-muted);
}
/* La lecture adaptée : l'énoncé puis chaque question dans son bloc, séparés d'un filet ; le texte
   en romain, aligné à gauche ; les données plus franches. La longueur des lignes, elle, se règle
   sur l'écran qui porte la feuille : une limite posée ici laissait le texte sur la moitié gauche
   d'une carte pleine largeur. */
.${RICH_TEXT_CLASS}.${ADAPTED_READING_CLASS} { text-align: left; }
.${ADAPTED_READING_CLASS} em,
.${ADAPTED_READING_CLASS} i { font-style: normal; }
.${ADAPTED_READING_CLASS} ol > li {
  padding-top: var(--enonce-block-gap);
  border-top: 1px solid var(--enonce-color-border);
}
.${ADAPTED_READING_CLASS} ol > li:not(:last-child) { margin-bottom: 0; padding-bottom: var(--enonce-block-gap); }
.${ADAPTED_READING_CLASS} ol > li::before {
  top: calc(var(--enonce-block-gap) + (var(--enonce-line-height) * 1em - var(--enonce-number-size)) / 2);
}
.${ADAPTED_READING_CLASS} .enonce-donnee { border-width: 1.5px; border-color: var(--enonce-color-muted); }
/* Aérées en hauteur ; en largeur, la marge du standard : à 21 px, trois colonnes d'en-têtes
   espacés débordaient d'un téléphone de 360 px, et la WebView ne défile pas de côté. */
.${ADAPTED_READING_CLASS} th,
.${ADAPTED_READING_CLASS} td { padding: var(--enonce-pad-sm); }
.${ADAPTED_READING_CLASS} tr:nth-child(even) td { background: var(--enonce-color-surface); }
/* Le message : une liste numérotée de Sam reste une liste — ses numéros, sans pastilles. Le gras
   change de famille (Outfit, une famille par graisse). */
.${MESSAGE_CLASS} ol { list-style: decimal; padding-left: var(--enonce-list-indent); }
.${MESSAGE_CLASS} ol > li { padding-left: 0; }
.${MESSAGE_CLASS} ol > li::before { content: none; }
.${MESSAGE_CLASS} strong,
.${MESSAGE_CLASS} b,
.${MESSAGE_CLASS} h1,
.${MESSAGE_CLASS} h2,
.${MESSAGE_CLASS} h3,
.${MESSAGE_CLASS} th { font-family: var(--enonce-font-family-bold); }
`
